import { Link } from 'expo-router';
import Calendar from 'lucide-react-native/icons/calendar';
import Clock from 'lucide-react-native/icons/clock';
import Heart from 'lucide-react-native/icons/heart';
import MapPin from 'lucide-react-native/icons/map-pin';
import { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { CardImage } from '@/components/riviera/card-image';
import { Badge, BadgeVariant } from '@/components/ui/badge';
import { Fonts, Radius, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { localizedTitle } from '@todo/core/i18n/event-content';
import { usableImageUrl } from '@todo/core/image-url';
import { currencySymbol, formatDayMonth, formatTimeRange } from '@todo/core/i18n/format';
import { useLocale } from '@todo/core/i18n/use-locale';
import type { DbEvent } from '@todo/core/supabase';

type Translate = (key: string, options?: Record<string, unknown>) => string;

const tagVariant: Record<string, BadgeVariant> = {
  concerts: 'destructive',
  exhibitions: 'primary',
  parties: 'chart4',
  theatre: 'gold',
  gastronomy: 'chart4',
};

function formatPrice(min: number | null, currency: string | null, t: Translate) {
  if (min == null) return '';
  const symbol = currencySymbol(currency);
  return t('common.fromPrice', { price: `${symbol}${min}` });
}

// Fixed-size event card shared by the events carousel on native and web. No
// breakpoint logic — the card is always 240px wide — so it lives in one file
// both platform variants import. One card per show: it links to the show page
// and, for a recurring show, counts the dates after the one it names.
export function EventCard({ event, eager = false }: { event: DbEvent; eager?: boolean }) {
  const [saved, setSaved] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();

  return (
    <Link
      href={{ pathname: '/[lang]/events/[id]', params: { lang: locale, id: event.show_slug } }}
      asChild>
      <Pressable style={({ pressed }) => [styles.cardPressable, pressed && { opacity: 0.85 }]}>
      <View
        style={[
          styles.card,
          { backgroundColor: theme.card, borderColor: theme.border },
        ]}>
      <View style={styles.imageBox}>
        <CardImage
          uri={usableImageUrl(event.image_url)}
          alt={localizedTitle(event, locale)}
          eager={eager}
        />
        <View style={styles.badgePos}>
          <Badge variant={tagVariant[event.category_id ?? ''] ?? 'primary'}>
            {event.category_id
              ? t(`categoryLabels.${event.category_id}`, { defaultValue: t('categoryLabels.fallback') })
              : t('categoryLabels.fallback')}
          </Badge>
        </View>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={saved ? t('a11y.removeFavorite') : t('a11y.addFavorite')}
          onPress={() => setSaved((v) => !v)}
          style={({ pressed }) => [
            styles.heartBtn,
            { backgroundColor: theme.card },
            pressed && { opacity: 0.7 },
          ]}>
          <Heart
            size={16}
            color={saved ? theme.destructive : theme.text}
            fill={saved ? theme.destructive : 'transparent'}
          />
        </Pressable>
      </View>
      <View style={styles.body}>
        <Text
          numberOfLines={2}
          style={[styles.title, { color: theme.text, fontFamily: Fonts?.serif }]}>
          {localizedTitle(event, locale)}
        </Text>
        <View style={styles.metaRow}>
          <Calendar size={13} color={theme.mutedForeground} />
          <Text style={[styles.metaText, { color: theme.mutedForeground }]}>
            {formatDayMonth(new Date(event.start_ts), event.tz, locale)}
            {(event.date_count ?? 1) > 1
              ? ` · ${t('events.moreDates', { count: (event.date_count ?? 1) - 1 })}`
              : ''}
          </Text>
        </View>
        <View style={styles.metaRow}>
          <Clock size={13} color={theme.mutedForeground} />
          <Text style={[styles.metaText, { color: theme.mutedForeground }]}>
            {formatTimeRange(event.start_ts, event.end_ts, event.tz, locale)}
          </Text>
        </View>
        <View style={styles.metaRow}>
          <MapPin size={13} color={theme.mutedForeground} />
          <Text
            numberOfLines={1}
            style={[styles.metaText, { color: theme.mutedForeground, flex: 1 }]}>
            {[event.venue_name, event.city_id].filter(Boolean).join(', ')}
          </Text>
        </View>
        <Text style={[styles.price, { color: theme.gold }]}>
          {formatPrice(event.min_price, event.currency, t)}
        </Text>
      </View>
      </View>
      </Pressable>
    </Link>
  );
}

const styles = StyleSheet.create({
  cardPressable: {
    width: 240,
    flexGrow: 0,
    flexShrink: 0,
  },
  card: {
    width: 240,
    borderRadius: Radius.xl,
    borderWidth: 1,
    overflow: 'hidden',
  },
  imageBox: {
    // Near-square: awin_fnac (currently ~all live events) supplies fixed
    // 222x222 poster images, so a landscape box like tour cards' 140px would
    // crop most of every poster. Matching the source's ~1:1 shape keeps the
    // whole poster visible.
    height: 220,
    width: '100%',
    position: 'relative',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  badgePos: {
    position: 'absolute',
    top: Spacing.two,
    left: Spacing.two,
  },
  heartBtn: {
    position: 'absolute',
    top: Spacing.two,
    right: Spacing.two,
    width: 30,
    height: 30,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    padding: Spacing.three - 2,
    gap: 4,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 20,
    minHeight: 40,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaText: {
    fontSize: 12,
  },
  price: {
    marginTop: 4,
    fontSize: 13,
    fontWeight: '700',
  },
});
